import React, { Fragment, useState, useEffect } from 'react'
import moment from 'moment'
import { Dialog, Transition } from '@headlessui/react'
import { ClockIcon, DocumentTextIcon, PencilIcon, PlusIcon, TrashIcon, UserIcon, XIcon } from '@heroicons/react/outline'
import classNames from 'classnames'
import { endsWith, get, startCase } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import Selection from 'components/molecules/Selection'
import notify from 'components/atoms/Notify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

const BookingNotesDrawer = ({ isNotesDrawerOpen, closeDrawer, selectedBooking }) => {
  const { t } = useTranslation()
  const [bookingNotes, setBookingNotes] = useState([])
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [noteForm, setNoteForm] = useState({ note: '', viewableBy: 'admin' })
  const [loading, setLoading] = useState(false)
  const [predefinedNotes, setPredefinedNotes] = useState([])
  const [predefinedNotesLoading, setPredefinedNotesLoading] = useState(false)

  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const canSeePredefinedNotes = hasAdminAccess || isCompanyRole

  const fetchBookingNotes = async () => {
    if (selectedBooking?._id) {
      try {
        const response = await apiGet(`/booking/${selectedBooking._id}/notes`)
        if (response?.success) {
          setBookingNotes(response?.data?.list || [])
        }
      } catch (error) {
        console.error('Failed to fetch booking notes:', error)
      }
    }
  }

  const fetchPredefinedNotes = async () => {
    if (!canSeePredefinedNotes) return

    try {
      setPredefinedNotesLoading(true)
      const params = {
        pageNumber: 1,
        pageSize: 100,
      }
      const response = await apiGet('notes', params)

      if (response?.success && response?.data?.notes) {
        const allNotes = response.data.notes || []
        const allowedNotes = new Set()

        if (hasAdminAccess) {
          allowedNotes.add('admin')
        }
        if (isCompanyRole) {
          allowedNotes.add('office')
        }
        if (allowedNotes.size > 0) {
          allowedNotes.add('all')
        }

        const filteredNotes = allNotes.filter(note => allowedNotes.has(note.notesFor))
        setPredefinedNotes(filteredNotes)
      } else {
        setPredefinedNotes([])
      }
    } catch (error) {
      console.error('Failed to fetch predefined notes:', error)
      setPredefinedNotes([])
    } finally {
      setPredefinedNotesLoading(false)
    }
  }

  const handleAddNote = async () => {
    // eslint-disable-next-line
    if (!noteForm.note.trim()) return

    setLoading(true)
    try {
      const payload = {
        ...noteForm,
        viewableBy: isCompanyRole ? 'office' : noteForm.viewableBy,
      }
      const response = await apiPost(`/booking-note/${selectedBooking._id}`, payload)
      if (response?.success) {
        await fetchBookingNotes()
        setNoteForm({ note: '', viewableBy: hasAdminAccess ? 'admin' : 'office' })
        setIsAddingNote(false)
      }
    } catch (error) {
      console.error('Failed to add note:', error)
    }
    setLoading(false)
  }

  const handleUpdateNote = async () => {
    // eslint-disable-next-line
    if (!noteForm.note.trim()) return
    setLoading(true)
    try {
      const payload = {
        ...noteForm,
        viewableBy: isCompanyRole ? 'office' : noteForm.viewableBy,
      }
      const response = await apiPut(`/booking-note/${editingNote._id}`, payload)
      if (response?.success) {
        await fetchBookingNotes()
        setEditingNote(null)
        setNoteForm({ note: '', viewableBy: hasAdminAccess ? 'admin' : 'office' })
        setIsAddingNote(false)
      }
    } catch (error) {
      console.error('Failed to update note:', error)
    }
    setLoading(false)
  }

  const handleDeleteNote = async noteId => {
    try {
      const response = await apiDelete(`/booking-note/${noteId}`)
      if (response?.success) {
        await fetchBookingNotes()
        notify.success(t('bookingNotes.messages.noteDeletedSuccessfully'))
      }
    } catch (error) {
      notify.error(t('bookingNotes.messages.failedToDeleteNote'))
    }
  }

  const startEditNote = note => {
    setEditingNote(note)
    setNoteForm({ note: note.note, viewableBy: note.viewableBy })
    setIsAddingNote(true)
  }

  const cancelNoteAction = () => {
    setIsAddingNote(false)
    setEditingNote(null)
    setNoteForm({ note: '', viewableBy: hasAdminAccess ? 'admin' : 'office' })
  }

  const getVisibilityTranslation = viewableBy => {
    const visibilityMap = {
      admin: t('bookingNotes.visibilityOptions.admin'),
      user: t('bookingNotes.visibilityOptions.user'),
      office: t('bookingNotes.visibilityOptions.office'),
      all: t('bookingNotes.visibilityOptions.all'),
    }
    return visibilityMap[viewableBy] || startCase(viewableBy)
  }

  useEffect(() => {
    if (isNotesDrawerOpen && selectedBooking) {
      fetchBookingNotes()
      fetchPredefinedNotes()
    }
  }, [isNotesDrawerOpen, selectedBooking, hasAdminAccess, isCompanyRole])

  useEffect(() => {
    if (!isNotesDrawerOpen) {
      cancelNoteAction()
      setBookingNotes([])
    }
  }, [isNotesDrawerOpen])

  const shouldShowPredefinedNotes = canSeePredefinedNotes && (predefinedNotesLoading || predefinedNotes.length > 0)

  return (
    <Transition.Root show={isNotesDrawerOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-hidden z-50" onClose={closeDrawer}>
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* Drawer Panel */}
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-2xl">
                {/* Close Button */}
                <div className="absolute top-0 right-0 -mr-12 pt-4">
                  <button
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-all duration-200"
                    onClick={closeDrawer}
                  >
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>

                <div className="h-full flex flex-col bg-white shadow-2xl">
                  <div className="bg-dark-600 px-6 py-6">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-xl font-semibold text-white flex-1">{t('bookingNotes.title', { userName: selectedBooking?.user?.fullName })}</Dialog.Title>
                      <button
                        className="md:hidden flex items-center justify-center h-8 w-8 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-all duration-200 ml-4"
                        onClick={closeDrawer}
                      >
                        <XIcon className="h-5 w-5 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden">
                    <div className="h-full flex flex-col">
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        {!isAddingNote ? (
                          <button
                            onClick={() => setIsAddingNote(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            {t('bookingNotes.buttons.addNewNote')}
                          </button>
                        ) : (
                          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <h4 className="text-lg font-medium text-gray-900">{editingNote ? t('bookingNotes.buttons.editNote') : t('bookingNotes.buttons.addNote')}</h4>
                            </div>

                            {shouldShowPredefinedNotes && (
                              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-100 rounded-md mt-3">
                                {predefinedNotesLoading ? (
                                  <span className="text-xs text-gray-500">{t('bookingNotes.labels.predefinedNotesLoading', 'Loading notes...')}</span>
                                ) : (
                                  predefinedNotes.map(note => (
                                    <button
                                      key={note._id}
                                      type="button"
                                      onClick={() => {
                                        setNoteForm(current => {
                                          const existing = current.note || ''
                                          const noteText = note.notes || ''
                                          if (!noteText) return current

                                          const separator = existing ? (endsWith(existing, '\n') ? '' : '\n') : ''
                                          return {
                                            ...current,
                                            note: `${existing}${separator}${noteText}`,
                                          }
                                        })
                                      }}
                                      className="px-3 py-1 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-100 rounded-full shadow-sm hover:bg-blue-100 hover:border-blue-200 transition-colors duration-150"
                                    >
                                      <span className="whitespace-nowrap">{note.notes}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            )}

                            <div className="mt-4">
                              <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('bookingNotes.labels.noteContent')}
                              </label>
                              <textarea
                                value={noteForm.note}
                                onChange={e => setNoteForm({ ...noteForm, note: e.target.value })}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder={t('bookingNotes.placeholders.enterNoteHere')}
                              />
                            </div>

                            {hasAdminAccess && (
                              <div className="mt-4">
                                <label htmlFor="viewable" className="block text-sm font-medium text-gray-700 mb-2">
                                  {t('bookingNotes.labels.viewableBy')}
                                </label>
                                <Selection
                                  onChange={value => {
                                    if (value && value.id) {
                                      setNoteForm({ ...noteForm, viewableBy: value.id })
                                    }
                                  }}
                                  title={t('bookingNotes.labels.selectVisibility')}
                                  list={[
                                    { id: 'user', name: t('bookingNotes.visibilityOptions.user') },
                                    { id: 'office', name: t('bookingNotes.visibilityOptions.office') },
                                    { id: 'all', name: t('bookingNotes.visibilityOptions.all') },
                                  ]}
                                  value={noteForm.viewableBy || ''}
                                  className="w-full"
                                />
                              </div>
                            )}

                            <div className="mt-4 flex space-x-3">
                              <button
                                onClick={editingNote ? handleUpdateNote : handleAddNote}
                                // eslint-disable-next-line
                                disabled={loading || !noteForm.note.trim()}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                              >
                                {editingNote ? t('bookingNotes.buttons.updateNote') : t('bookingNotes.buttons.saveNote')}
                              </button>
                              <button
                                onClick={cancelNoteAction}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                              >
                                {t('bookingNotes.buttons.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 overflow-y-auto px-6 py-4">
                        {bookingNotes.length === 0 ? (
                          <div className="text-center py-12">
                            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('bookingNotes.emptyState.title')}</h3>
                            <p className="text-gray-500">{t('bookingNotes.emptyState.description')}</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {bookingNotes.map(note => (
                              <div key={note._id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                      <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                                        <UserIcon className="h-4 w-4 text-gray-600" />
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{note.updatedBy?.fullName || ''}</p>
                                      <p className="text-xs text-gray-500">{moment(note.createdAt).format('MMM DD, YYYY at h:mm A')}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span
                                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        note.viewableBy === 'admin'
                                          ? 'bg-red-100 text-red-800'
                                          : note.viewableBy === 'user'
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-green-100 text-green-800'
                                      }`}
                                    >
                                      {getVisibilityTranslation(note.viewableBy)}
                                    </span>
                                    {hasAdminAccess && (
                                      <div className="flex space-x-1">
                                        <button
                                          onClick={() => startEditNote(note)}
                                          className="p-1 text-gray-400 hover:text-blue-500 transition-colors duration-200"
                                          title={t('bookingNotes.actions.editNote')}
                                        >
                                          <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteNote(note._id)}
                                          className="p-1 text-gray-400 hover:text-red-500 transition-colors duration-200"
                                          title={t('bookingNotes.actions.deleteNote')}
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                                  {note.note}
                                  {note.isEdit && <span className="ml-2 text-xs text-gray-500 italic">{t('bookingNotes.status.edited')}</span>}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default BookingNotesDrawer
